import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';
import { MenuItem, MenuCategory } from '../queries/menu.queries';

// Category mutations
export interface CreateCategoryInput {
  name: {
    en: string;
    th: string;
    zh?: string;
  };
  displayOrder: number;
  isActive: boolean;
}

export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateCategoryInput) => {
      const response = await apiClient.post<APIResponse<{ category: MenuCategory }>>(
        '/menu/categories',
        input
      );
      return response.data.data.category;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
}

export function useUpdateCategory(categoryId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: Partial<CreateCategoryInput>) => {
      const response = await apiClient.patch<APIResponse<{ category: MenuCategory }>>(
        `/menu/categories/${categoryId}`,
        input
      );
      return response.data.data.category;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
}

export function useDeleteCategory(categoryId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.delete<APIResponse<{ success: boolean }>>(
        `/menu/categories/${categoryId}`
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
}

// Menu item mutations
export interface CreateMenuItemInput {
  categoryId: string;
  name: {
    en: string;
    th: string;
    zh?: string;
  };
  description?: {
    en: string;
    th: string;
    zh?: string;
  };
  basePrice: number;
  availableSizes: ('small' | 'medium' | 'large')[];
  sizePricing?: {
    small?: number;
    medium?: number;
    large?: number;
  };
  imageUrl?: string;
  isAvailable: boolean;
  isRecommended: boolean;
  tags?: string[];
  modifierGroups?: string[];
}

export function useCreateMenuItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateMenuItemInput) => {
      const response = await apiClient.post<APIResponse<{ item: MenuItem }>>(
        '/menu/items',
        input
      );
      return response.data.data.item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-items'] });
    },
  });
}

export function useUpdateMenuItem(itemId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: Partial<CreateMenuItemInput>) => {
      const response = await apiClient.patch<APIResponse<{ item: MenuItem }>>(
        `/menu/items/${itemId}`,
        input
      );
      return response.data.data.item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-items'] });
    },
  });
}

export function useDeleteMenuItem(itemId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.delete<APIResponse<{ success: boolean }>>(
        `/menu/items/${itemId}`
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-items'] });
    },
  });
}

// Modifier group mutations
export interface CreateModifierGroupInput {
  name: {
    en: string;
    th: string;
    zh?: string;
  };
  type: 'size' | 'sweetness' | 'topping' | 'custom';
  isRequired: boolean;
  minSelections: number;
  maxSelections: number;
  options: Array<{
    name: {
      en: string;
      th: string;
      zh?: string;
    };
    priceAdjustment: number;
    isAvailable: boolean;
  }>;
}

export function useCreateModifierGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateModifierGroupInput) => {
      const response = await apiClient.post<APIResponse<{ group: any }>>(
        '/menu/modifiers',
        input
      );
      return response.data.data.group;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modifiers'] });
    },
  });
}

export function useUpdateModifierGroup(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: Partial<CreateModifierGroupInput>) => {
      const response = await apiClient.patch<APIResponse<{ group: any }>>(
        `/menu/modifiers/${groupId}`,
        input
      );
      return response.data.data.group;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modifiers'] });
    },
  });
}

export function useDeleteModifierGroup(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.delete<APIResponse<{ success: boolean }>>(
        `/menu/modifiers/${groupId}`
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modifiers'] });
    },
  });
}
